import portfolioData from '../data/portfolio.json';

/**
 * Portfolio data utilities and validation
 */

// Project schema validation
const PROJECT_SCHEMA = {
  required: ['id', 'name', 'filename', 'description', 'category'],
  optional: ['stage', 'socialLinks']
};

/**
 * Validates a single project object against the schema
 * @param {Object} project - Project object to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateProject = (project) => {
  const errors = [];
  
  // Check required fields
  PROJECT_SCHEMA.required.forEach(field => {
    if (!project[field]) {
      errors.push(`Missing required field: ${field}`);
    }
  });
  
  // Validate specific field types
  if (project.id && typeof project.id !== 'string') {
    errors.push('Project ID must be a string');
  }
  
  if (project.category && !portfolioData.categories.find(cat => cat.id === project.category)) {
    errors.push(`Invalid category: ${project.category}`);
  }
  
  if (project.socialLinks) {
    const validSocialPlatforms = ['twitter', 'website', 'discord', 'telegram', 'linkedin', 'youtube', 'reddit', 'linktree'];
    Object.keys(project.socialLinks).forEach(platform => {
      if (!validSocialPlatforms.includes(platform)) {
        errors.push(`Invalid social platform: ${platform}`);
      }
      
      // Basic URL validation
      const url = project.socialLinks[platform];
      if (url && !url.startsWith('http')) {
        errors.push(`Invalid URL for ${platform}: ${url}`);
      }
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Gets all portfolio projects
 * @returns {Array} - Array of project objects
 */
export const getAllProjects = () => {
  return portfolioData.projects;
};

/**
 * Gets projects filtered by category
 * @param {string} categoryId - Category ID to filter by
 * @returns {Array} - Filtered array of project objects
 */
export const getProjectsByCategory = (categoryId) => {
  if (categoryId === 'all') {
    return portfolioData.projects;
  }
  return portfolioData.projects.filter(project => project.category === categoryId);
};

/**
 * Gets all available categories
 * @returns {Array} - Array of category objects
 */
export const getCategories = () => {
  return portfolioData.categories;
};

/**
 * Gets a single project by ID
 * @param {string} projectId - Project ID
 * @returns {Object|null} - Project object or null if not found
 */
export const getProjectById = (projectId) => {
  return portfolioData.projects.find(project => project.id === projectId) || null;
};

/**
 * Dynamic image import utility
 * @param {string} imageName - Image filename
 * @returns {Promise} - Promise resolving to image module
 */
export const importProjectImage = async (imageName) => {
  if (!imageName) {
    console.warn('Image filename is undefined, using default placeholder');
    return { default: '/images/placeholder.png' };
  }
  
  try {
    const module = await import(`../assets/workedIcon/${imageName}`);
    return module;
  } catch (error) {
    console.error(`Error loading image: ${imageName}`, error);
    return { default: '/images/placeholder.png' };
  }
};

/**
 * Prepares project data for display (with loaded images)
 * @param {Array} projects - Array of project objects
 * @returns {Promise<Array>} - Promise resolving to projects with loaded images
 */
export const prepareProjectsForDisplay = async (projects) => {
  const imagePromises = projects.map(project => importProjectImage(project.filename));
  const images = await Promise.all(imagePromises);
  
  return projects.map((project, index) => ({
    ...project,
    icon: images[index].default
  }));
};

/**
 * Gets portfolio statistics
 * @returns {Object} - Portfolio statistics
 */
export const getPortfolioStats = () => {
  const projects = portfolioData.projects;
  const categories = portfolioData.categories.slice(1); // Exclude "all" category
  
  const stats = {
    totalProjects: projects.length,
    categoriesBreakdown: categories.map(category => ({
      category: category.name,
      count: projects.filter(p => p.category === category.id).length
    })),
    stageBreakdown: {}
  };
  
  // Calculate stage breakdown
  projects.forEach(project => {
    const stage = project.stage || 'Unknown';
    stats.stageBreakdown[stage] = (stats.stageBreakdown[stage] || 0) + 1;
  });
  
  return stats;
};

/**
 * Template for adding a new project
 * @returns {Object} - Empty project template
 */
export const getNewProjectTemplate = () => {
  return {
    id: '',
    name: '',
    filename: '',
    description: '',
    category: 'DeFi',
    stage: 'Seed',
    socialLinks: {
      website: '',
      twitter: ''
    }
  };
};

/**
 * Validates the entire portfolio data structure
 * @returns {Object} - Validation results
 */
export const validatePortfolioData = () => {
  const results = {
    isValid: true,
    errors: [],
    warnings: [],
    projectValidations: []
  };
  
  // Validate each project
  portfolioData.projects.forEach((project, index) => {
    const validation = validateProject(project);
    results.projectValidations.push({
      index,
      project: project.name || `Project ${index + 1}`,
      ...validation
    });
    
    if (!validation.isValid) {
      results.isValid = false;
      results.errors.push(`Project "${project.name || index + 1}" has validation errors`);
    }
  });
  
  // Check for duplicate IDs
  const ids = portfolioData.projects.map(p => p.id);
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
  if (duplicateIds.length > 0) {
    results.isValid = false;
    results.errors.push(`Duplicate project IDs found: ${duplicateIds.join(', ')}`);
  }
  
  return results;
};